import { IconReportSearch } from '@tabler/icons-react'
import { IconX, IconArrowLeft } from '@tabler/icons-react'

import { useState, useRef, useEffect } from 'react'

import { Tooltip, Modal, Button, TextInput, Loader } from '@mantine/core'
import { useDebouncedValue } from '@mantine/hooks'

import { useAxiosApi } from '@/hooks/useAxiosApi'
import { Link, useLocation } from 'react-router-dom'
import { useListener } from 'react-bus'
import { HitMeta } from '@/components/ui/TypesenseGlobalSearch'

import {
    Container, Clock, Package, ShoppingBag01, LogIn01, Tool01,
} from '@untitled-ui/icons-react'

const ENTITY_CONFIG = {
    component: { label: 'Composant', icon: Container, index: 'components' },
    production_order: { label: 'Assemblage', icon: Clock, index: 'production_orders' },
    fulfillment_order: { label: 'Préparation', icon: Package, index: 'fulfillment_orders' },
    order: { label: 'Commande', icon: ShoppingBag01, index: 'orders' },
    purchase_order: { label: 'Bon de commande', icon: LogIn01, index: 'purchase_orders' },
    service_case: { label: 'SAV', icon: Tool01, index: 'service_cases' },
}

const AZERTY_DIGITS = {
    '&': '1', 'é': '2', '"': '3', "'": '4', '(': '5',
    '-': '6', '§': '6', 'è': '7', '_': '8', '!': '8', 'ç': '9', 'à': '0',
}

const normalizeNumber = (value) => Array.from(value, (character) => AZERTY_DIGITS[character] ?? character)
    .join('').replace(/\D/g, '').slice(0, 9)

export default function NumberLookupSearch({ hideTrigger = false }) {
    const [isOpened, setIsOpened] = useState(false)
    const [number, setNumber] = useState('')
    const [results, setResults] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const inputRef = useRef(null)
    const { requestGet } = useAxiosApi()

    const [debouncedNumber] = useDebouncedValue(number, 300)

    const open = () => {
        setIsOpened(true)
        setTimeout(() => {
            inputRef.current?.focus()
            inputRef.current?.select()
        }, 100)
    }

    const close = () => {
        setIsOpened(false)
        setNumber('')
        setResults([])
    }

    // Listen for bus event
    useListener('openNumberLookup', open)

    // Close on page change
    const location = useLocation()

    useEffect(() => {
        close()
    }, [location])

    // Fetch results on debounced number change
    useEffect(() => {
        if (!Number(debouncedNumber)) {
            setResults([])
            setIsLoading(false)
            return
        }

        let cancelled = false
        setIsLoading(true)

        requestGet(`/lookup/${debouncedNumber}`).then((res) => {
            if (!cancelled) {
                setResults(res.data || [])
                setIsLoading(false)
            }
        }).catch(() => {
            if (!cancelled) {
                setResults([])
                setIsLoading(false)
            }
        })

        return () => { cancelled = true }
    }, [debouncedNumber])

    // Show loading immediately on typing
    useEffect(() => {
        if (number && number !== debouncedNumber) {
            setIsLoading(true)
        }
    }, [number, debouncedNumber])

    const onNumberClick = (n) => {
        setNumber((current) => normalizeNumber(current + String(n)))
    }

    const handleNumberChange = (event) => {
        const input = event.currentTarget
        const value = input.value
        const caret = normalizeNumber(value.slice(0, input.selectionStart ?? value.length)).length
        const nextValue = normalizeNumber(value)

        // Normalize the DOM value before React reconciles it to preserve the caret.
        input.value = nextValue
        input.setSelectionRange(caret, caret)
        setNumber(nextValue)
    }

    const clearNumber = () => {
        setNumber('')
    }

    const backNumber = () => {
        setNumber((current) => current.slice(0, -1))
    }

    return (
        <>
            {!hideTrigger && (
                <Tooltip label="Rechercher par numéro">
                    <button
                        className="h-full px-3 bg-white hover:bg-gray-50 flex items-center justify-center"
                        onClick={open}
                    >
                        <IconReportSearch size={26} stroke={1.8} />
                    </button>
                </Tooltip>
            )}

            <Modal
                keepMounted
                opened={isOpened}
                onClose={close}
                title="Rechercher par numéro"
            >
                <div className="flex flex-col gap-5">
                    <span className="text-gray-400">
                        Indiquer un numéro pour rechercher un composant, un assemblage, une préparation, une commande, un bon de commande ou un SAV.
                    </span>

                    <TextInput
                        ref={inputRef}
                        value={number}
                        onChange={handleNumberChange}
                        inputMode="numeric"
                        pattern="[0-9]*"
                    />

                    <div className="grid grid-cols-3 gap-2 mx-auto">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((item) => (
                            <Button
                                radius={100}
                                h={80}
                                w={80}
                                key={item}
                                variant="default"
                                onClick={() => onNumberClick(item)}
                                size="lg"
                            >
                                {item}
                            </Button>
                        ))}

                        <Button
                            variant="light"
                            color="dark"
                            onClick={clearNumber}
                            radius={100}
                            h={80}
                            w={80}
                        >
                            <IconX />
                        </Button>

                        <Button
                            radius={100}
                            h={80}
                            w={80}
                            variant="default"
                            onClick={() => onNumberClick(0)}
                            size="lg"
                        >
                            0
                        </Button>

                        <Button
                            variant="light"
                            color="dark"
                            onClick={backNumber}
                            radius={100}
                            h={80}
                            w={80}
                        >
                            <IconArrowLeft />
                        </Button>
                    </div>

                    {isLoading && (
                        <div className="flex justify-center py-4">
                            <Loader size={20} />
                        </div>
                    )}

                    {!isLoading && results.length > 0 && (
                        <div className="border rounded-xl overflow-hidden divide-y">
                            {results.map((result) => {
                                const config = ENTITY_CONFIG[result.type]
                                if (!config) return null
                                const Icon = config.icon

                                return (
                                    <Link
                                        key={result.type + '-' + result.id}
                                        to={result.url}
                                        className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50/60 transition-colors"
                                    >
                                        <Icon className="shrink-0 w-4 h-4 text-gray-400" />
                                        <div className="flex flex-col min-w-0 flex-1">
                                            <span className="text-[13px] text-gray-800 truncate leading-snug">
                                                {result.name}
                                            </span>
                                            <span className="text-[11px] text-gray-400">
                                                {config.label}
                                            </span>
                                        </div>
                                        <div className="shrink-0">
                                            <HitMeta index={config.index} hit={result} />
                                        </div>
                                    </Link>
                                )
                            })}
                        </div>
                    )}

                    {!isLoading && debouncedNumber > 0 && results.length === 0 && (
                        <div className="text-gray-500 text-sm text-center py-6">
                            Aucun résultat
                        </div>
                    )}
                </div>
            </Modal>
        </>
    )
}
